import PageLayout from '../../common/components/PageLayout/PageLayout'
import TextRef from '../../common/components/TextRef/TextRef'
import Title from '../../common/components/Title/Title'
import DataTable from 'react-data-table-component'
import './Results.css'
import { columns, data } from './datatable'
import TextSeparator from '../../common/components/TextSeparator/TextSeparator'
import ImageWithCaption from '../../common/components/ImageWithCaption/ImageWithCaption'
import Images from '../../common/Images'
import Mosaic from './components/Mosaic'
import References from '../../common/components/References/References'

function Results () {
  return (
    <PageLayout title="Results">
      <Title isMid title="Experiments Overview" />
      <p>
        Amongst our goals, we wanted to express our A antigen and B antigen
        degrading enzymes respectively P0DTR4{' '}
        <TextRef
          sup="1"
          content="(N-acetyl-&alpha;-D-galactosamine deacetylase)"
        />
        , P0DTR5
        <TextRef
          sup="1"
          content=" (&alpha;-D-galactosamine galactosaminidase)"
        />{' '}
        and GH110b
        <TextRef sup="2" content=" (&alpha;-galactosidase)" /> in comparison to
        previous iGEM team enzymes (iGEM Taipei 2021), namely NAGA
        <TextRef sup="3" content=" (&alpha;-N-Acetylgalactosaminidase)" /> and
        GH110a
        <TextRef sup="2" content=" (&alpha;-galactosidase)" />. For that
        purpose, we were able to clone, express and purify the proteins P0DTR5,
        GH110b and GH110a and confirm the &alpha;-galactosidase activity.
      </p>

      <TextSeparator />
      <div className="results-datatable">
        <DataTable
          columns={columns}
          data={data}
          title="Table 1. List and description of enzymes used in the project"
          responsive
        />
        <br />
        <span>
          Molecular weight and isoelectric points were calculated using Expasy
          web tools.
        </span>
      </div>
      <br />
      <br />
      <p>
        To better implement the enzymatic removal of antigens using our selected
        enzymes, we designed a fluidic tubular device in order “to filter out”
        the antigens of blood passing through the device. We performed fluid
        dynamics to predict the flow of blood in different device geometry to
        obtain the best performing system.
      </p>

      <p>
        In order to go further than previous iGEM teams, we brought our interest
        to other important immunogenic groups. We thus decided to extend our
        strategy to the Rh immunogenic group. Due to the lack of clear
        understanding of the structure of the Rh complex and the protein
        segments containing epitopes related to the immune response. In that
        regard, we predicted the structure of each main subunit of the Rh
        complex, namely RhD, RhCE and RhAG. We assembled each monomer in
        different combinations of dimers and trimers, and then verified their
        affinity and stability in a mammalian plasma membrane.
      </p>
      <TextSeparator />
      <Title isMid title="Wet lab - Genetic constructs" />
      <p>
        We designed our codon optimized synthetic genes, P0DTR4, P0DTR5 and
        GH110b, as well as iGEM Taipei 2021 genes, NAGA and GH100a with
        6XHis-Tag and 5&apos; NdeI and 3&apos; BamHI restriction site for
        directional cloning into pET-3a (<b>Figure 1</b>).
      </p>
      <ImageWithCaption
        figure={1}
        caption="Schematised principle of cloning of DNA constructs."
        src={Images.Results1}
        imagePadding={20}
        captionPadding={0}
      />
      <p>
        We thus proceeded with the cloning of each DNA construct onto pET-3a in{' '}
        <i>E. coli</i> DH5α. Only pET-3a-P0DTR5, pET-3a-GH110b and pET-3a-GH110a
        after several troubleshooting iterations. We then transformed the three
        plasmids in <i>E. coli</i> BL21 (DE3) for later protein expression
        assays. However, even after 48 hours of growth, significant growth
        defects and even absence of growth was observed. Growing selected
        colonies lead to no growth, even with extended incubation over seven
        days. That suggested some promoter leaking of our genes, leading to
        either toxic activity within the cell or proteotoxicity. In order to
        resolve the apparent toxicity, we transformed our plasmids into{' '}
        <i>E. coli</i> Rosetta (DE3) pLysS in hope of reducing the amount of
        unfolded protein, if proteotoxicity is at stake. This proved to be
        useful as growth defects were resolved and plasmid extraction and PCR
        confirmed the identity of each plasmid.
      </p>
      <TextSeparator />
      <Title isMid title="Wet lab - Proteins expression and purification" />
      <p>
        With our three plasmids, we proceeded with the expression assays. With
        overnight pre cultures, we inoculated 400 mL of LB 100 µg/mL ampicillin,
        20 µg/mL chloramphenicol with 4 mL of cultures. Cultures were grown
        between 0.5 and 0.8 OD<sub>600</sub> prior to IPTG induction at a
        concentration of 1 mM. We stored the pellets before induction for
        SDS-PAGE in order to verify Incubation was continued for 18 hours prior
        to total protein extraction using sonication. 1 mL of each culture was
        conserved as a total protein extract, whereas the rest of cultures were
        pelleted and lysed. Lysis was carried out using a sonicator (Model 505
        Sonic Dismembrator (Fisher FB-505) with the 1/4-inch probe (FB4420) with
        pulses of 10 seconds, with pauses of 20 seconds between pulses for a
        total of 10 minutes. Insoluble extracts were kept, whereas soluble
        extracts were purified on 1 mL nickel IMAC column. Even though the
        column we had could withstand up to 5 mM of DTT, our column was
        drastically reduced. We still carried out the purification, even though
        the oxidation degree of nickel was surely altered, thus the affinity
        reduced.
      </p>
      <p>
        Following the purification, a quick qualitative Bradford assay confirmed
        there was indeed protein for each purification. 200 µL of Bradford
        reagent with 790 µL of milliQ water was added to 10 µL of samples in
        Eppendorf tubes (Final volume: 1 mL). A control was also used by mixing
        200 µL of Bradford reagent with 800 µL of milliQ water (Final volume: 1
        mL). This confirmed there was indeed protein for each purification due
        to samples turning bluish (<b>Figure 2</b>).
      </p>
      <ImageWithCaption
        figure={2}
        caption="Qualitative Bradford assay of purified protein extracts."
        src={Images.Results2}
        imagePadding={20}
        captionPadding={0}
      />
      <p>
        We further analyzed soluble and insoluble extracts as well as purified
        extract by SDS-PAGE electrophoresis to confirm the identity of our
        proteins and the quality of purification (<b>Figure 3</b>, <b>4</b>).
      </p>
      <ImageWithCaption
        figure={3}
        caption="SDS-PAGE electrophoresis of lysis extracts."
        src={Images.Results3}
        imagePadding={20}
        captionPadding={0}
      />
      <p>
        The figure shows the electrophoresis of insoluble (pellet) and soluble
        (lysate) extracts before purification, as well as the flow through
        samples. Comparing insoluble and soluble extracts, GH110a appears to be
        mostly found in the insoluble pellet, consistent with previous
        <TextRef sup="2" content=" report" />. GH110b and P0DTR5 can be found in
        the pellet, however in a significantly lower proportion than GH110a.
        This suggests that the former proteins are more stably expressed and
        folded under our induction expression protocol. iGEM Taipei 2021 also
        experienced difficulties with expressing GH110a.
      </p>
      <ImageWithCaption
        figure={4}
        caption="SDS-PAGE electrophoresis of purification steps."
        src={Images.Results4}
        imagePadding={20}
        captionPadding={0}
      />
      <p>
        Purified extracts of the three proteins (P0DTR5, GH110b and GH110a) all
        appears in the purified extract (shown by white arrows). Including
        His-tags, P0DTR5 has a theoretical molecular weight of 119,530 KDa seen
        in well 7, GH110b of 67.834 KDa seen in well 9 and GH110a of 69.143 KDa
        seen in well 3. Low amounts of the aforementioned proteins appears in
        the wash extracts. Significant amounts of residual non specifically
        bound proteins remained in the purified extract. These remarks are not
        surprising considering the altered affinity of the column due to DTT in
        samples and purification buffers.
      </p>
      <p>
        We then did a buffer exchange of our purified extract and concentrated
        our proteins by using Amicon® Ultra 0.5 mL Centrifugal Filters (10K). We
        started by centrifuging all of the samples in the Amicon with 400 µL of
        volume at 13 000 rpm for 10 minutes for each cycle. After each
        centrifuge, we collected the flow through from the collecting tube in
        eppendorf tubes. When we finished centrifuging all of the samples, we
        proceeded to add 400 µL 10 mM Tris buffer pH 7.5 in the filter tube and
        centrifuged the Amicon at 13 000 rpm for 10 minutes. We did that step
        three times (total volume: 1.5 mL). We then collected the protein from
        the filter tube (app. 200 µL) by doing gentle “ups and downs”. In order
        to collect all the proteins, we did a reverse spin by inverting the
        filter tube in the collection tube and centrifuging at 13 000 rpm for 10
        minutes. We then used the collected proteins for enzymatic assays.
      </p>
      <TextSeparator />
      <Title isMid title="Wet lab - Enzymatic assays" />
      <p>
        In consideration of the significant amount of contaminant proteins in
        our purified extract mostly due to the degraded affinity of IMAC column,
        we were not able to characterize the specific affinity (K<sub>m</sub>)
        and catalytic activity (K<sub>cat</sub>) of our enzymes. However, we
        wanted to confirm that at least our enzymes were functional after
        extraction and purification. We thus tested for B antigen degradation
        activity (&alpha;-galactosidase) using an adapted protocol from the iGEM
        Taipei 2021 team. We added 10 µL of enzyme respectively to 50 µL of 10
        mM 4-nitrophenyl &alpha;-D-galactopyranoside and 40 µL of 200 mM Tris
        buffer pH 7.5. This confirmed that both our GH110b and iGEM Taipei 2021
        GH110a were functional. GH110b gave a quick and intense signal compared
        to the GH110a, consistent with the previous
        <TextRef sup="2" content=" report" />. However, this is subject to
        variability, considering the uncertainty of active enzymes for each
        reaction. The absence of significant signal in control without proteins
        and the control with P0DTR5 (that is not active towards 4-nitrophenyl
        &alpha;-D-galactopyranoside) confirms that the signal does not arise
        from either spontaneous hydrolysis of substrate or non-specific
        &alpha;-galactosidase activity from contaminant protein.
      </p>
      <ImageWithCaption
        figure={5}
        caption="Qualitative &alpha;-galactosidase assay."
        src={Images.Results5}
        imagePadding={20}
        captionPadding={0}
      />
      <p>
        A fix volume of purified extracts was distributed in reactions and not a
        same amount of enzyme. After three hours of incubation at room
        temperature, both GH110b (this work) and GH110a (iGEM Taipei 2021)
        reacted with the chromogenic analogue sugar suggesting that both enzymes
        were active after purification and buffer exchange. Two negative
        controls were made for that purpose; a negative control containing no
        proteins (to verify the spontaneous hydrolysis in the reaction mix) and
        a negative control containing proteins that should not react (P0DTR5).
      </p>
      <p>
        Further experiments of protein expression and enzymatic assays in the
        conditions of our fluidic system would enable us to obtain the K
        <sub>m</sub> and K<sub>cat</sub> of each enzyme, including the ones we
        were not able to express in <i>E. coli</i>. This would allow us to
        better understand and implement our enzyme in the fluidic system we
        designed.
      </p>
      <TextSeparator />
      <Title isMid title="Dry-lab - Rh structure predictions" />
      <p>
        With no structure available, we went with a <i>de novo</i> approach with
        homology elements, as there are related proteins in the Rh protein
        family. We did a sequence alignment, secondary structure and disorder
        predictions, and 3D structure predictions of monomers and multimers.
      </p>
      <Title isSub title="Sequence alignment" />
      <p>
        We used the Blast suite tool COBALT to perform sequence alignments with
        the Rh protein family. The sequences used were:
      </p>
      <b className="results-bold">Human proteins</b>
      <ul className="results-ul">
        <li>
          Two RhD isoforms, RhD1 (NCBI Reference Sequence: NP_057208.3) and RhD7
          (NCBI Reference Sequence: NP_001269800.1);
        </li>
        <li>RhAG (UniProt accession number: Q02094);</li>
        <li>RhCE (GenBank: ANT95300.1)</li>
        <li>RhCG (UniProtKB/Swiss-Prot: Q9UBD6.1)</li>
        <li>RhBG (UniProtKB/Swiss-Prot: Q9H310.3)</li>
      </ul>

      <b className="results-bold">Amoeba proteins</b>
      <ul className="results-ul">
        <li>RhGA (UniProtKB/Swiss-Prot: Q9NIV0.1)</li>
        <li>RhGB (UniProtKB/Swiss-Prot: Q8I0L6.1)</li>
      </ul>
      <p>
        We performed a global alignment between all query sequences and
        one-on-one alignments between the human Rh proteins. The global
        alignment revealed multiple well-conserved regions between the different
        proteins, most probably the transmembrane helixes. The local alignment
        confirmed that RhAG is the erythrocyte surface protein most different
        from the others.
      </p>
      <div className="results-images">
        <ImageWithCaption
          figure={6}
          caption="Graphic representation of the alignment between RhD1 and RhD7."
          src={Images.Graph1}
          imagePadding={20}
          captionPadding={0}
        />
        <ImageWithCaption
          figure={7}
          caption="Graphic representation of the alignment between RhD7 and RhCE."
          src={Images.Graph2}
          imagePadding={20}
          captionPadding={0}
        />
      </div>
      <div className="results-images results-images-bottom">
        <ImageWithCaption
          figure={8}
          caption="Graphic representation of the alignment between RhD1 et RhCE."
          src={Images.Graph3}
          imagePadding={20}
          captionPadding={0}
        />
        <ImageWithCaption
          figure={9}
          caption="Graphic representation of the alignment between RhAG and RhCE."
          src={Images.Graph4}
          imagePadding={20}
          captionPadding={0}
        />
      </div>
      <br />
      <br />
      <b className="results-bold">Secondary structure prediction</b>
      <p>
        After comparing the sequences, we predicted the structural motifs of the
        erythrocyte Rh proteins using
        <TextRef sup="4" content=" PSIPRED" /> and the disordered regions using
        <TextRef sup="5" content=" DISOPRED" />. The only structured motifs were
        alpha helixes, with about a dozen in each protein. The very few
        disordered regions were short and in random coil regions, and therefore
        are not cause for concern.
      </p>
      <p>
        The transmembrane segments were predicted with the
        <TextRef sup="6" content=" DeepTMHMM" /> tool. All helixes are
        transmembrane, with cytosolic N and C-terminal ends.
      </p>
      <br />
      <br />
      <b className="results-bold">3D structure prediction</b>
      <p>
        We used
        <TextRef sup="7" content=" ColabFold" /> to test all possible monomers,
        dimers and trimers of the 3 Rh subunits (RhD, RhCE, RhAG) with
        <TextRef sup="8" content=" AlphaFold2 Multimer" />. We used the standard
        parameters, with use of dropout in the deep learning steps.
      </p>
      <p>
        After creating all 20 protein models, we evaluated their binding energy
        with
        <TextRef sup="9" content=" FoldX" /> in a way that reflects biological
        reality. The most stable multimers were these trimers: RhAG homotrimer,
        2xRhAG with RhCE, RhD homotrimer and 2xRhAG with RhD.
      </p>
      <br />
      <Title isSub title="Molecular dynamics" />
      <p>
        The sequence alignment, as well as the secondary and tertiary
        predictions, indicated that RhCE and RhD are extremely similar. To gain
        a better understanding of their structure and their interactions in
        biological conditions, we performed molecular dynamics.
      </p>
      <p>
        A bilayer membrane and protein system for each of the two stable
        heterotrimers with RhCE and RhD was constructed with
        <TextRef sup="10" content=" OPM PPM3" /> and
        <TextRef sup="11" content=" Charmm-Gui" />.
      </p>
      <p>
        Using the NAMD molecular dynamics program
        <TextRef sup="12" content=" program" />, we ran molecular dynamics
        simulations of 200 ns for both systems. System parameters were:
      </p>
      <ul className="results-ul">
        <li>
          200 lipids for the outer membrane leaflets, and 154 lipids for the
          inner membrane leaflets. The mammalian plasma membrane lipid
          composition was previously reported in Pogozheva et al., 2022.
        </li>
        <li>
          A water layer of 30 Å on top and bottom of the membrane and Rh trimer
          system.
        </li>
        <li>A simulation temperature of 303.15 K.</li>
        <li>Concentration of 0.15 M of NaCL ions and 0.15 M of KCl ions.</li>
        <li>The other default parameters were used.</li>
      </ul>
      <TextSeparator />
      <Title isMid title="Computational Fluid Dynamics" />
      <span className="results-tip">
        <b>Tip: </b>click figures to expand them!
      </span>
      <Mosaic
        combos={[
          {
            graph: Images.CDF1Graph1,
            pipe: Images.CDF1Pipe1
          },
          {
            graph: Images.CDF1Graph2,
            pipe: Images.CDF1Pipe2
          },
          {
            graph: Images.CDF1Graph3,
            pipe: Images.CDF1Pipe3
          },
          {
            graph: Images.CDF1Graph4,
            pipe: Images.CDF1Pipe4
          },
          {
            graph: Images.CDF1Graph5,
            pipe: Images.CDF1Pipe5
          },
          {
            graph: Images.CDF1Graph6,
            pipe: Images.CDF1Pipe6
          },
          {
            graph: Images.CDF1Graph7,
            pipe: Images.CDF1Pipe7
          },
          {
            graph: Images.CDF1Graph8,
            pipe: Images.CDF1Pipe8
          },
          {
            graph: Images.CDF1Graph9,
            pipe: Images.CDF1Pipe9
          }
        ]}
      />
      <p>
        Initial CFD analyzes showed that the parameters we chose to investigate
        appeared to have an important effect on the overall behavior of the
        fluid flow within the system. Baffles created localized important higher
        velocity zones and larger baffles seemed to create larger zones. Baffles
        also created dead zones of lower velocities at their endpoints or close
        to them. These dead zones are to be expected when using such baffles and
        only pose a problem if they significantly impair the flow
        <TextRef sup="13" content=" stream" />. Maximum and average velocity
        varied significantly between models. This change can in great part be
        attributed to the varying initial pressure values we used when designing
        the models. The wide range of velocity profiles we obtained coupled with
        the use of pressure as a variable made the use of velocity as a proxy
        for turbulence and homogeneity significantly accurate.
      </p>
      <Mosaic
        combos={[
          {
            graph: Images.CDF2Graph1,
            pipe: Images.CDF2Pipe1
          },
          {
            graph: Images.CDF2Graph2,
            pipe: Images.CDF2Pipe2
          },
          {
            graph: Images.CDF2Graph3,
            pipe: Images.CDF2Pipe3
          },
          {
            graph: Images.CDF2Graph4,
            pipe: Images.CDF2Pipe4
          },
          {
            graph: Images.CDF2Graph5,
            pipe: Images.CDF2Pipe5
          },
          {
            graph: Images.CDF2Graph6,
            pipe: Images.CDF2Pipe6
          },
          {
            graph: Images.CDF2Graph7,
            pipe: Images.CDF2Pipe7
          },
          {
            graph: Images.CDF2Graph8,
            pipe: Images.CDF2Pipe8
          },
          {
            graph: Images.CDF2Graph9,
            pipe: Images.CDF2Pipe9
          }
        ]}
      />
      <p>
        The analyzes done on models simulated at a stable pressure of 10 psi
        revealed that models with closer and wider baffles have a higher average
        velocity. Models that were initially simulated with higher pressure
        values were seen to have much more consistent velocity profiles at low
        pressure. The slower flow rate through these models also led to smaller
        dead zones created by the baffles. However, longer baffles seemed to
        create smaller dead zones at their endpoints.
      </p>
      <Mosaic
        combos={[
          {
            graph: Images.CDF3Graph,
            pipe: Images.CDF3Pipe,
            pressure: Images.CDF3Pressure
          }
        ]}
      />
      <p>
        Further analyses done on the model 4, which seemed to perform better by
        visual assessment of flow rate homogeneity and deadzone evaluation,
        showed spots of low pressure caused by{' '}
        <TextRef sup="14" content="backflow" /> lead to small increases in
        viscosity that did not appear to significantly alter the flow. An
        analysis of pressure as a function of position also revealed that it
        diminished as the flow progressed further in the system. Small zones of
        low pressure can be seen in spots where backflow occurs as different
        streams of fluid collide and create low pressure areas.
      </p>

      <p>
        Zones of reverse flow, while not a major problem in the design of such
        systems, are still undesirable as they can have adverse effects on
        overall <TextRef sup="14" content="flows" />. Here they do not
        significantly affect the overall velocity profile to an undesirable
        extent but they will still have to be taken into consideration during
        the next steps of the design process.
      </p>

      <References
        references={[
          <span key={1}>
            Rahfeld P, Sim L, Moon H, Constantinescu I, Morgan-Lang C, Hallam
            SJ, Kizhakkedathu JN, Withers SG. An enzymatic pathway in the human
            gut microbiome that converts A to universal O type blood. Nat
            Microbiol. 2019 Sep;4(9):1475-1485. doi: 10.1038/s41564-019-0469-7.
            Epub 2019 Jun 10. PMID: 31182795.
          </span>,
          <span key={2}>
            Liu QP, Yuan H, Bennett EP, Levery SB, Nudelman E, Spence J, Pietz
            G, Saunders K, White T, Olsson ML, Henrissat B, Sulzenbacher G,
            Clausen H. Identification of a GH110 subfamily of alpha
            1,3-galactosidases: novel enzymes for removal of the alpha 3Gal
            xenotransplantation antigen. J Biol Chem. 2008 Mar
            28;283(13):8545-54. doi: 10.1074/jbc.M709020200. Epub 2008 Jan 28.
            PMID: 18227066; PMCID: PMC2417185.
          </span>,
          <span key={3}>
            Liu QP, Sulzenbacher G, Yuan H, Bennett EP, Pietz G, Saunders K,
            Spence J, Nudelman E, Levery SB, White T, Neveu JM, Lane WS, Bourne
            Y, Olsson ML, Henrissat B, Clausen H. Bacterial glycosidases for the
            production of universal red blood cells. Nat Biotechnol. 2007
            Apr;25(4):454-64. doi: 10.1038/nbt1298. Epub 2007 Apr 1. PMID:
            17401360.
          </span>,
          <span key={5}>
            Buchan DWA, Jones DT (2019). The PSIPRED Protein Analysis Workbench:
            20 years on. Nucleic Acids Research.{' '}
            <i>https://doi.org/10.1093/nar/gkz297</i>
          </span>,
          <span key={6}>
            Jones DT, Cozzetto D. (2015). DISOPRED3: precise disordered region
            predictions with annotated protein-binding activity. Bioinformatics.
            2015 Mar 15;31(6):857-63. doi: 10.1093/bioinformatics/btu744. Epub
            2014 Nov 12.
          </span>,
          <span key={7}>
            Jeppe Hallgren, Konstantinos D. Tsirigos, Mads D. Pedersen, José
            Juan Almagro Armenteros, Paolo Marcatili, Henrik Nielsen, Anders
            Krogh and Ole Winther (2022). DeepTMHMM predicts alpha and beta
            transmembrane proteins using deep neural networks.{' '}
            <i>https://doi.org/10.1101/2022.04.08.487609</i>
          </span>,
          <span key={8}>
            Mirdita, M., Schütze, K., Moriwaki, Y. et al. ColabFold: making
            protein folding accessible to all. Nat Methods 19, 679-682 (2022).{' '}
            <i>https://doi.org/10.1038/s41592-022-01488-1</i>
          </span>,
          <span key={9}>
            Evans, R. et al. (2022). Protein complex prediction with
            AlphaFold-Multimer (p. 2021.10.04.463034). bioRxiv.{' '}
            <i>https://doi.org/10.1101/2021.10.04.463034</i>
          </span>,
          <span key={10}>
            Schymkowitz J, Borg J, Stricher F, Nys R, Rousseau F, Serrano L. The
            FoldX web server: an online force field. Nucleic Acids Res. 2005 Jul
            1;33(Web Server issue):W382-8. doi: 10.1093/nar/gki387. PMID:
            15980494; PMCID: PMC1160148.
          </span>,
          <span key={11}>
            Lomize AL, Todd SC, Pogozheva ID. Spatial arrangement of proteins in
            planar and curved membranes by PPM 3.0. Protein Sci. 2022
            Jan;31(1):209-220. doi: 10.1002/pro.4219. Epub 2021 Nov 8. PMID:
            34716622; PMCID: PMC8740824.
          </span>,
          <span key={12}>
            Jo S, Kim T, Iyer VG, Im W. CHARMM-GUI: a web-based graphical user
            interface for CHARMM. J Comput Chem. 2008;29(11):1859-1865.
            doi:10.1002/jcc.20945. PMID: 18351591.
          </span>,
          <span key={13}>
            Phillips JC, Hardy DJ, Maia JDC, et al. Scalable molecular dynamics
            on CPU and GPU architectures with NAMD. J Chem Phys.
            2020;153(4):044130. doi:10.1063/5.0014475. PMID: 32752662.
          </span>,
          <span key={14}>
            Sierra-Espinosa, F. Z., Amjad, S., Carrillo, F., Soria, J., &
            Atkinson, C. (2022). Turbulent cylinder-stirred flow heat and
            momentum transfer research in batch operated single-phase square
            reactor. International Journal of Thermal Sciences, 172, 107325.{' '}
            <i>https://doi.org/10.1016/j.ijthermalsci.2021.107325</i>
          </span>,
          <span key={15}>
            Sun, J., Mi, Z., Lu, C., Cao, X., & Ding, M. (2020). Experimental
            research on reverse flow phenomenon in an equal-height-difference
            natural circulation system at low pressure. Nuclear Engineering and
            Design, 370, 110882.{' '}
            <i>https://doi.org/10.1016/j.nucengdes.2020.110882</i>
          </span>
        ]}
      />
    </PageLayout>
  )
}

export default Results
